import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface UseOcTextInputParams {
  optional?: boolean;
  defaultValue?: string;
}

export interface UseOcTextInputResult {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  error: boolean;
  setError: Dispatch<SetStateAction<boolean>>;
  helperText: string;
  setHelperText: Dispatch<SetStateAction<string>>;
  isValid: () => boolean;
  validate: () => void;
}

/**
 * Hook for managing a text input with optional validation.
 *
 * @param optional - If true, skips required-field validation.
 * @param defaultValue - The initial value of the input.
 */
export const useOcTextInput = ({
  optional = false,
  defaultValue = "",
}: UseOcTextInputParams): UseOcTextInputResult => {
  const { t }: { t: TFunction } = useTranslation();
  const [value, setValue] = useState<string>(defaultValue);
  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>("");

  // Reset value whenever defaultValue changes
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const isValid = (): boolean => {
    if (optional) return true;
    return value.trim() !== "";
  };

  const validate = (): void => {
    if (optional) {
      setError(false);
      setHelperText("");
      return;
    }
    if (value.trim() === "") {
      setError(true);
      setHelperText(t("OC_ERROR_MESSAGE_FIELD_IS_REQUIRED"));
    } else {
      setError(false);
      setHelperText("");
    }
  };

  return {
    value,
    setValue,
    error,
    setError,
    helperText,
    setHelperText,
    isValid,
    validate,
  };
};

export default useOcTextInput;
